import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;
`;

export const LoginForm = styled.form`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  padding: 3rem 2.5rem;
  border-radius: 32px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 440px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  animation: ${fadeIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1);
`;

export const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0.5rem;
  background: white;
  padding: 1rem;
  border-radius: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

export const Img = styled.img`
  height: 80px;
  width: auto;
`;

export const Title = styled.h1`
  text-align: center;
  color: #0f172a;
  margin: 0;
  font-size: 1.875rem;
  font-weight: 800;
  letter-spacing: -0.025em;
`;

export const SubTitle = styled.p`
  text-align: center;
  color: #64748b;
  margin: 0.5rem 0 1.5rem;
  font-size: 0.9375rem;
  line-height: 1.5;
  font-weight: 500;
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 700;
  color: #475569;
  margin-left: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const Input = styled.input`
  width: 100%;
  height: 52px;
  padding: 0 1.25rem;
  font-size: 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  color: #0f172a;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f8fafc;
    cursor: not-allowed;
  }
`;

export const PasswordInput = styled(Input)`
  padding-right: 3.5rem;
`;

export const PasswordWrapper = styled.div`
  position: relative;
`;

export const ToggleButton = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.08);
  }
`;

export const Button = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.25);
  margin-top: 0.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.3);
    opacity: 0.95;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

export const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fee2e2;
  color: #dc2626;
  padding: 0.875rem;
  border-radius: 12px;
  font-size: 0.875rem;
  text-align: center;
  margin: 0.5rem 0;
  font-weight: 500;
`;

// Unsupported Screen Styles
export const UnsupportedContainer = styled(LoginContainer)``;

export const UnsupportedCard = styled(LoginForm)`
  text-align: center;
  align-items: center;
  max-width: 480px;
`;

export const UnsupportedIcon = styled.div`
  width: 80px;
  height: 80px;
  background: rgba(225, 29, 72, 0.05);
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  border: 1px solid rgba(225, 29, 72, 0.2);

  svg {
    width: 40px;
    height: 40px;
    stroke: #e11d48;
  }
`;

export const UnsupportedTitle = styled(Title)`
  margin-bottom: 1rem;
`;

export const UnsupportedText = styled.p`
  color: #64748b;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0 0 2rem 0;
`;

export const UnsupportedStep = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  text-align: left;
  padding: 0.5rem 0 0.5rem 0;
  background: white;
  border-radius: 20px;
  border: 1px solid #f1f5f9;
  width: 100%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: #3b82f6;
    transform: translateX(8px);
    box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.1);
  }

  .step-number {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 0.875rem;
    flex-shrink: 0;
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.2);
  }

  .step-text {
    color: #0f172a;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.4;
  }
`;

export const QRCodePlaceholder = styled.div`
  width: 120px;
  height: 120px;
  background: white;
  padding: 10px;
  border-radius: 20px;
  border: 2px dashed #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1.5rem 0;
  color: #94a3b8;
  position: relative;

  &::after {
    content: 'QR CODE';
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.1em;
  }
`;
